import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Truck,
  CalendarPlus,
  PiggyBank,
  Receipt,
  FileBarChart,
  TrendingUp,
  Banknote,
  Wallet,
  Sparkles,
  StickyNote,
  Landmark,
  LogOut,
  Menu,
  Upload,
  LineChart as LineChartIcon,
} from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import CalculatorWidget from "@/components/CalculatorWidget";
import { useAuth } from "@/auth/AuthProvider";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

const links = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/vehicles", label: "Vehicles", icon: Truck, testid: "nav-vehicles" },
  { to: "/daily-entry", label: "Daily Entry", icon: CalendarPlus, testid: "nav-daily-entry" },
  { to: "/sales", label: "Total Sales", icon: TrendingUp, testid: "nav-sales" },
  { to: "/deposit", label: "Syndicate Deposit", icon: Banknote, testid: "nav-deposit" },
  { to: "/earnings", label: "Net Earnings", icon: Wallet, testid: "nav-earnings" },
  { to: "/extra-income", label: "Extra Income", icon: Sparkles, testid: "nav-extra-income" },
  { to: "/expenses", label: "Expenses", icon: Receipt, testid: "nav-expenses" },
  { to: "/profit", label: "Profit", icon: LineChartIcon, testid: "nav-profit" },
  { to: "/savings", label: "Savings Fund", icon: PiggyBank, testid: "nav-savings" },
  { to: "/notes", label: "Important Notes", icon: StickyNote, testid: "nav-notes" },
  { to: "/investments", label: "Investments", icon: Landmark, testid: "nav-investments" },
  { to: "/reports", label: "Reports", icon: FileBarChart, testid: "nav-reports" },
  { to: "/bulk-import", label: "Bulk Import", icon: Upload, testid: "nav-bulk-import" },
];

function SidebarBody({ onNavigate }) {
  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-gray-200 flex items-center gap-3">
        <img
          src="/vivan-logo.jpg"
          alt="Vivan Paribahan"
          className="h-10 w-10 rounded-md object-cover shrink-0"
        />
        <div className="min-w-0">
          <div className="label-eyebrow leading-none">Fleet OS</div>
          <h1 className="font-display text-base font-black tracking-tight truncate mt-1">
            Vivan Paribahan
          </h1>
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            data-testid={l.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                isActive ? "bg-black text-white" : "text-gray-700 hover:bg-gray-100"
              }`
            }
          >
            <l.icon className="h-4 w-4" />
            <span>{l.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-gray-200">
        <CalculatorWidget />
      </div>
    </div>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  if (!user) return null;
  const initial = (user.name || user.email || "?").trim().charAt(0).toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-9 px-1.5 gap-2 rounded-full"
          data-testid="user-menu-btn"
        >
          <Avatar className="h-7 w-7">
            {user.picture ? <AvatarImage src={user.picture} alt={user.name} /> : null}
            <AvatarFallback className="text-xs bg-black text-white">
              {initial}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60" data-testid="user-menu">
        <DropdownMenuLabel className="space-y-0.5">
          <div className="text-sm font-semibold truncate">{user.name || "Owner"}</div>
          <div className="text-xs text-gray-500 font-mono truncate">{user.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={logout}
          className="text-red-600 focus:text-red-700 gap-2"
          data-testid="logout-btn"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function AppShell({ children }) {
  const [sheetOpen, setSheetOpen] = useState(false);

  return (
    <div className="min-h-screen flex bg-white">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-64 lg:w-72 shrink-0 border-r border-gray-200 flex-col">
        <SidebarBody />
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-gray-200 flex items-center justify-between px-3 md:px-6 sticky top-0 bg-white/95 backdrop-blur z-10">
          <div className="flex items-center gap-2 min-w-0">
            {/* Mobile hamburger */}
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden -ml-1"
                  data-testid="mobile-menu-btn"
                  aria-label="Open menu"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="p-0 w-72"
                data-testid="mobile-sidebar"
              >
                <SheetTitle className="sr-only">Navigation</SheetTitle>
                <SidebarBody onNavigate={() => setSheetOpen(false)} />
              </SheetContent>
            </Sheet>

            <div className="md:hidden flex items-center gap-2 min-w-0">
              <img
                src="/vivan-logo.jpg"
                alt="Vivan Paribahan"
                className="h-7 w-7 rounded-md object-cover shrink-0"
              />
              <div className="font-display font-black tracking-tight truncate">
                Vivan Paribahan
              </div>
            </div>
            <div className="hidden md:block label-eyebrow">
              Commercial Vehicle Operations
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <NotificationBell />
            <UserMenu />
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6 lg:p-8 max-w-[1400px] w-full">
          {children}
        </main>
      </div>
    </div>
  );
}
